import React from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { useAuth } from 'react-oidc-context';

/**
 * This component handles the OAuth callback from Cognito
 * The actual token exchange is handled automatically by react-oidc-context
 * We just need to provide a loading UI while that happens
 */
const AuthCallback: React.FC = () => {
  const auth = useAuth();
  
  // Force redirect to dashboard when authentication is complete
  React.useEffect(() => {
    // If authentication is complete (not loading and no error), redirect to dashboard
    if (!auth.isLoading && !auth.error && auth.isAuthenticated) {
      console.log('Authentication successful, redirecting to dashboard');
      
      // Small delay to ensure tokens are processed
      const redirectTimer = setTimeout(() => {
        // Clear any stored paths to prevent loops
        localStorage.removeItem('originalPath');
        
        // Force redirect to dashboard
        window.location.href = '/dashboard';
      }, 1000);
      
      return () => clearTimeout(redirectTimer);
    }
  }, [auth.isLoading, auth.error, auth.isAuthenticated]);
  
  // Handle different auth states
  if (auth.isLoading) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}>
        <Paper sx={{ p: 4, width: '100%', maxWidth: '400px', m: 2, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Completing Sign In
          </Typography>
          <Typography variant="body2">
            Please wait while we verify your authentication...
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  // Handle authentication errors
  if (auth.error) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}>
        <Paper sx={{ p: 4, width: '100%', maxWidth: '500px', m: 2, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body1">
            {auth.error.message}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Please try again or contact support if the problem persists.
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  // If we're not loading and there's no error, show success message while redirect happens
  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    }}>
      <Paper sx={{ p: 4, width: '100%', maxWidth: '400px', m: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="success.main" gutterBottom>
          Authentication Successful
        </Typography>
        <Typography variant="body2">
          You will be redirected momentarily...
        </Typography>
      </Paper>
    </Box>
  );
};

export default AuthCallback;
